/**
 * 🗄️ Employee Management data cache
 *
 * React state resets every time this page unmounts (switching tabs,
 * navigating away and back, etc.), which previously forced a brand new
 * round-trip for employees/counts/companies/archived/clients on every
 * single visit — even if the user had loaded the exact same data seconds
 * earlier. This module lives outside the React tree so it survives
 * unmount/remount within the same browser session.
 *
 * - Fresh (within TTL) results are served instantly from memory instead of
 *   hitting the network again.
 * - Concurrent requests for the same key are deduplicated — if two hooks
 *   (or two effects on the same hook) ask for the same data at the same
 *   time, only one network call actually goes out.
 * - Callers pass `force: true` after a mutation (add/edit/delete/import/
 *   restore) to bypass the cache and guarantee fresh data.
 */

const DEFAULT_TTL_MS = 30 * 1000; // 30s — short enough to stay accurate, long enough to kill duplicate fetches on remount

const store = new Map(); // key -> { data, expiresAt }
const inFlight = new Map(); // key -> Promise

/**
 * Fetch `key`, transparently caching the result.
 *
 * @param {string} key - stable cache key (include clientName/viewType/etc.)
 * @param {() => Promise<any>} fetcher - performs the actual network request
 * @param {{ ttl?: number, force?: boolean }} [options]
 */
export function getCached(key, fetcher, { ttl = DEFAULT_TTL_MS, force = false } = {}) {
  if (!force) {
    const cached = store.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.data);
    }
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const promise = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      store.set(key, { data, expiresAt: Date.now() + ttl });
      inFlight.delete(key);
      return data;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

/**
 * Drop only the IN-FLIGHT (pending) entries, leaving completed cached data
 * alone.
 *
 * Why this exists: a pending promise here is shared by every caller of the
 * same key. If the component that started it aborts its fetch (on unmount, or
 * when superseding an older load), that promise is now permanently rejected —
 * but it stays in `inFlight` until its own rejection handler runs, which is
 * asynchronous. A caller that asks for the same key in between gets handed the
 * already-doomed promise and sees an AbortError for a request it never made.
 *
 * Under React StrictMode (mount -> unmount -> remount) that is not a race, it
 * is the guaranteed order: the remount's fetch always lands before the aborted
 * promise settles, so the page loads nothing at all.
 *
 * Call this immediately after aborting. Completed entries are deliberately
 * preserved so returning to the page is still instant.
 */
export function dropInFlight(prefix) {
  [...inFlight.keys()].forEach((key) => {
    if (!prefix || key.startsWith(prefix)) inFlight.delete(key);
  });
}

/**
 * Drop cached entries. With no prefix, clears everything; otherwise clears
 * only keys starting with `prefix` (e.g. invalidateEmployeeCache("employees:")).
 */
export function invalidateEmployeeCache(prefix) {
  const keys = new Set([...store.keys(), ...inFlight.keys()]);
  keys.forEach((key) => {
    if (!prefix || key.startsWith(prefix)) {
      store.delete(key);
      inFlight.delete(key);
    }
  });
}
